export const CAREER_APPLICATION_ENDPOINT = `${(
  process.env.REACT_APP_SUPABASE_URL || "https://ullvjzbhgoqsfjiopvem.supabase.co"
).replace(/\/$/, "")}/functions/v1/career-applications`;

export const APPLICATION_LIMITS = {
  name: 120,
  email: 254,
  github: 2048,
  linkedin: 2048,
  portfolio_url: 2048,
  work_url: 2048,
  code_url: 2048,
  proof: 10000,
  depth: 10000,
};

const isApplicationReference = (value) => typeof value === "string" && /^[a-f\d]{8}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{12}$/i.test(value);

export function newApplicationId() {
  if (!globalThis.crypto?.getRandomValues) throw new Error("Your browser could not prepare a secure application. Please update your browser or use the copy option below.");
  if (globalThis.crypto.randomUUID) return globalThis.crypto.randomUUID();
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function formatApplicationEvidence(form) {
  return [
    "Work or demo: " + form.work_url.trim(),
    "GitHub profile: " + form.github.trim(),
    ...(form.portfolio_url?.trim() ? ["Portfolio: " + form.portfolio_url.trim()] : []),
    ...(form.code_url?.trim() ? ["Code: " + form.code_url.trim()] : []),
    "", "Personal accomplishment and evidence:", form.proof.trim(),
  ].join("\n");
}

export function validateApplication(form) {
  const fields = { application_version: 2 };
  for (const [field, limit] of Object.entries(APPLICATION_LIMITS)) {
    fields[field] = String(form[field] || "").trim();
    if (!["portfolio_url", "code_url"].includes(field) && !fields[field]) {
      return { error: { field, message: field === "work_url" ? "Add a link to work or a demo we can review." : "Please complete this field." } };
    }
    if (fields[field].length > limit) {
      return { error: { field, message: `Please keep this field to ${limit.toLocaleString()} characters or fewer.` } };
    }
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email)) {
    return { error: { field: "email", message: "Enter a valid email address so we can contact you." } };
  }
  for (const field of ["github", "linkedin", "portfolio_url", "work_url", "code_url"]) {
    if (!fields[field]) continue;
    try {
      const input = fields[field];
      // Bare domains are common when pasting a profile. Explicit schemes must be HTTP(S).
      const hasScheme = /^[a-z][a-z\d+.-]*:/i.test(input);
      const url = new URL(hasScheme ? input : `https://${input}`);
      if (!/^https?:$/.test(url.protocol) || !url.hostname.includes(".") || url.username || url.password || /\s/.test(input)) throw new Error();
      if (field === "github" && (!/^(www\.)?github\.com$/i.test(url.hostname) || !/^\/[^/]+\/?$/.test(url.pathname))) throw new Error();
      if (field === "linkedin" && (!/(^|\.)linkedin\.com$/i.test(url.hostname) || !/^\/(in|pub)\/[^/]+/i.test(url.pathname))) throw new Error();
      fields[field] = url.href;
      if (fields[field].length > APPLICATION_LIMITS[field]) throw new Error();
    } catch {
      const messages = {
        github: "Enter your GitHub profile URL, such as https://github.com/username.",
        linkedin: "Enter your LinkedIn profile URL, such as https://linkedin.com/in/your-name.",
        portfolio_url: "Enter a valid portfolio URL, or leave this field blank.",
        work_url: "Enter a valid link to your work or demo, such as https://example.com/project.",
        code_url: "Enter a valid code or repository URL, or leave this field blank.",
      };
      return { error: { field, message: messages[field] } };
    }
  }
  const proof = formatApplicationEvidence(fields);
  if (proof.length > APPLICATION_LIMITS.proof) {
    return { error: { field: "proof", message: "Keep your explanation and evidence links to 10,000 characters in total." } };
  }
  // Keep the existing v2 storage, email and retry contract. Evidence links travel
  // inside proof; the endpoint rejects additional top-level fields.
  return { fields: {
    application_version: 2, name: fields.name, email: fields.email,
    linkedin: fields.linkedin, proof, depth: fields.depth,
  } };
}

export async function sendApplication(payload, { timeoutMs = 30000 } = {}) {
  const controller = new AbortController();
  let timeout;
  try {
    const { response, result } = await Promise.race([
      fetch(CAREER_APPLICATION_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: controller.signal,
      }).then(async (response) => ({ response, result: await response.json().catch(() => null) })),
      new Promise((_, reject) => {
        timeout = setTimeout(() => {
          controller.abort();
          reject(new Error("application_timeout"));
        }, timeoutMs);
      }),
    ]);
    if (!response.ok) {
      const serverError = result?.error;
      const error = new Error(
        typeof serverError?.message === "string" && serverError.message.length <= 400
          ? serverError.message
          : "We could not submit your application. Please try again.",
      );
      if (Object.hasOwn(APPLICATION_LIMITS, serverError?.field)) error.field = serverError.field;
      if (isApplicationReference(result?.application_id)) error.reference = result.application_id;
      if (typeof result?.retryable === "boolean") error.retryable = result.retryable;
      throw error;
    }
    if (result?.ok !== true || !isApplicationReference(result.application_id)) {
      throw new Error("We could not confirm receipt. Please try again; your application will not be duplicated.");
    }
    return result;
  } catch (error) {
    if (controller.signal.aborted || error?.name === "AbortError") {
      throw new Error("This is taking longer than expected. Your answers are saved here. Please try again; your application will not be duplicated.");
    }
    if (error instanceof TypeError) {
      throw new Error("We could not connect. Check your connection and try again. Your answers are saved here.");
    }
    throw error;
  } finally {
    clearTimeout(timeout);
  }
}
